<template>
  <LayupSpinner v-if="isLoading" :size="10" />
  <div v-show="!isLoading">
    <div>
      <h1 class="font-semibold text-xl text-grey-light-900">API & Integrations</h1>
      <p class="text-grey-light-600 text-sm">Integrate our API into your existing systems for a seamless experience.</p>
      <p v-if="errorMessage" class="text-red-700">{{ errorMessage }}</p>
    </div>
    <SectionDivider class="my-4" />
    <form @submit.prevent="onSubmit">
      <div class="grid gap-4 md:grid-cols-5 sm:grid-cols-1">
        <div class="col-span-2">
          <h5 class="font-semibold text-grey-light-900">Generate Keys</h5>
          <p class="text-grey-light-600 text-sm">We recommend re-generating API keys frequently.</p>
        </div>
        <div class="col-span-2">
          <div class="flex flex-col gap-4">
            <div class="space-y-1">
              <LabelComponent>API Key</LabelComponent>
              <p class="text-grey-light-600 text-sm">Generate your API key to get started.</p>
            </div>
            <div>
              <Button variant="primaryOutline" class="w-auto" @click.prevent="openApiKey()">Generate API Key</Button>
              <div class="text-grey-light-600 text-sm mt-2 flex" v-if="apiKey">
                <input
                  type="text"
                  placeholder="api key"
                  class="w-3/4 p-2 border rounded-lg mr-2"
                  :value="apiKey"
                  disabled
                />
                <Button variant="outline" size="icon" @click.prevent="copyKey(apiKey)">
                  <Copy class="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <SectionDivider class="my-4" />
      <div class="grid gap-4 md:grid-cols-5 sm:grid-cols-1">
        <div class="col-span-2">
          <h5 class="font-semibold text-grey-light-900">Webhooks</h5>
          <p class="text-grey-light-600 text-sm">Receive real-time notifications when events occur.</p>
        </div>
        <div class="col-span-2">
          <div class="flex flex-col gap-4">
            <div class="space-y-1">
              <LabelComponent>Orders</LabelComponent>
              <p class="text-grey-light-600 text-sm">
                Order web-hooks will send notifications when an Order changes state. Enter in the URL to be notified
                when the Orders web-hook is triggered.
              </p>
            </div>
            <FormField v-slot="{ componentField }" name="webhooks.ordersUrl">
              <FormItem>
                <FormControl>
                  <Input type="text" placeholder="Order URL" v-bind="componentField" />
                </FormControl>
                <FormMessage />
              </FormItem>
            </FormField>
            <div class="space-y-1">
              <LabelComponent>Payments</LabelComponent>
              <p class="text-grey-light-600 text-sm">
                Payment web-hooks will send notifications when a Payment changes state (Successful or Failed). Enter in
                the URL to be notified when the Payments web-hook is triggered.
              </p>
            </div>
            <FormField v-slot="{ componentField }" name="webhooks.paymentsUrl">
              <FormItem>
                <FormControl>
                  <Input type="text" placeholder="Payments URL" v-bind="componentField" />
                </FormControl>
                <FormMessage />
              </FormItem>
            </FormField>
          </div>
        </div>
      </div>
      <SectionDivider class="my-4" />
      <div class="flex justify-end gap-4 mt-4 mr-8">
        <Button class="w-auto" type="submit">Save</Button>
      </div>
    </form>
  </div>
  <Dialog :open="showConfirmationDialog" @update:open="showConfirmationDialog = $event">
    <DialogContent class="sm:max-w-[525px]">
      <DialogHeader>
        <DialogTitle>{{ title }}</DialogTitle>
        <DialogDescription>{{ description }}</DialogDescription>
      </DialogHeader>
      <DialogFooter>
        <Button type="button" variant="outline" class="bg-red-600 text-white" @click="showConfirmationDialog = false">
          Cancel
        </Button>
        <Button type="button" class="w-auto" @click.prevent="generateKey()">Yes</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>

<script setup lang="ts">
import LayupSpinner from '@/components/common/LayupSpinner.vue';
import SectionDivider from '@/components/common/SectionDivider.vue';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import LabelComponent from '@/components/ui/label/LabelComponent.vue';
import { NetworkError } from '@/errors';
import { generateApiKey } from '@/services/auth';
import { saveMerchant } from '@/services/merchants';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import { toTypedSchema } from '@vee-validate/zod';
import { Copy } from 'lucide-vue-next';
import { useForm } from 'vee-validate';
import { ref, watch } from 'vue';
import { z } from 'zod';

const props = defineProps<{
  settings: {
    paymentNotifyUrl?: string;
    notifyUrl?: string;
  };
  merchantId: string;
}>();
const emit = defineEmits(['save:merchant']);
const showConfirmationDialog = ref(false);
const title = ref('');
const description = ref('');
const isSuccess = ref(false);
const isLoading = ref(false);
const apiKey = ref('');
const errorMessage = ref('');

const apiIntegrationsFormSchema = toTypedSchema(
  z.object({
    webhooks: z
      .object({
        ordersUrl: z.string().optional(),
        paymentsUrl: z.string().optional(),
      })
      .superRefine((data, ctx) => {
        const urlPattern = /^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/i;

        if (data.ordersUrl) {
          if (!urlPattern.test(data.ordersUrl)) {
            ctx.addIssue({
              code: 'custom',
              path: ['ordersUrl'],
              message: 'Invalid URL format for orders URL, should start with: https:// or http://',
            });
          }
        }

        if (data.paymentsUrl) {
          if (!urlPattern.test(data.paymentsUrl)) {
            ctx.addIssue({
              code: 'custom',
              path: ['paymentsUrl'],
              message: 'Invalid URL format for payments URL, should start with: https:// or http://',
            });
          }
        }
      }),
  }),
);

const form = useForm({
  validationSchema: apiIntegrationsFormSchema,
});

watch(
  () => props.settings,
  (newSettings) => {
    if (!newSettings) {
      isLoading.value = false;
      return;
    }

    isLoading.value = true;

    form.setValues({
      webhooks: {
        ordersUrl: newSettings.notifyUrl,
        paymentsUrl: newSettings.paymentNotifyUrl,
      },
    });

    // Silent validation won't display errors till user submits or touches a field
    form.validate({ mode: 'silent' });
    isLoading.value = false;
  },
  { immediate: true, deep: true },
);

const openApiKey = () => {
  showConfirmationDialog.value = true;
  title.value = 'Generate API Key';
  description.value =
    'Regenerating your api key will cause a disruption to any existing integrations. Are you sure you would like to proceed?';
};

const generateKey = async () => {
  try {
    isSuccess.value = false;
    isLoading.value = true;
    errorMessage.value = '';
    const response = await generateApiKey();
    apiKey.value = response.apikey!;
    isSuccess.value = true;
    showConfirmationDialog.value = false;
    // Need to set form values again because state is lost when loader is shown
    form.setValues({
      webhooks: {
        ordersUrl: props.settings.notifyUrl,
        paymentsUrl: props.settings.paymentNotifyUrl,
      },
    });
  } catch (error: any) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('api-integration-page-organization', {
        status: 'network_error',
        channel: 'merchant',
        method: 'saveMerchant',
      });
    } else {
      createException(error);
      addMetric('api-integration-page-organization', { status: 'error', channel: 'merchant', method: 'saveMerchant' });
    }
    errorMessage.value = 'Something went wrong. Please try again later';
    if (error.response && error.response.data) {
      errorMessage.value = error.response.data;
    }
    showConfirmationDialog.value = false;
  } finally {
    isLoading.value = false;
  }
};

const copyKey = (key: string) => {
  navigator.clipboard.writeText(key);
  apiKey.value = '';
};

const onSubmit = async () => {
  isLoading.value = true;
  const { values, errors, valid } = await form.validate();
  errorMessage.value = '';
  // Need to manually check errors because for some reason vee-validate doesn't set errors on the form when submitted even if valid is false
  if (Object.keys(errors).length > 0) {
    Object.entries(errors).forEach(([key, value]) => {
      form.setFieldError(key as keyof typeof errors, value);
    });
    return;
  }

  if (!valid) {
    errorMessage.value = 'Please fill in all required fields';
    return;
  }

  try {
    isLoading.value = true;
    if (props.merchantId === '') {
      errorMessage.value = 'Merchant ID is not set. Please try reloading the page';
      return;
    }

    const response = await saveMerchant({
      id: props.merchantId,
      modelMerchantRequest: { paymentNotifyUrl: values?.webhooks?.paymentsUrl, notifyUrl: values?.webhooks?.ordersUrl },
    });
    emit('save:merchant', response);

    addMetric('api-integration-page-organization', { status: 'success', channel: 'merchant', method: 'saveMerchant' });
  } catch (error: any) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('api-integration-page-organization', {
        status: 'network_error',
        channel: 'merchant',
        method: 'saveMerchant',
      });
    } else {
      createException(error);
      addMetric('api-integration-page-organization', { status: 'error', channel: 'merchant', method: 'saveMerchant' });
    }
    errorMessage.value = 'Something went wrong. Please try again later';
    if (error.response && error.response.data) {
      errorMessage.value = error.response.data;
    }
  }
};
</script>
